<template>
  <div class="gen-edit" v-if="row || !isEdit">
    <!-- TOPBAR -->
    <header class="topbar">
      <div class="left">
        <button class="btn btn-ghost" @click="$router.push(backRoute)">
          ← Back
        </button>
        <div class="title-wrap">
          <div class="pill">#{{ form.id_backoffice || 'new' }}</div>
          <h1 class="title" :title="form.name || '(untitled)'">
            {{ form.name || '(untitled)' }}
          </h1>

          <!-- AI/Human badge (TOP) -->
          <div class="gen-origin-chip" :class="origin">
            <span v-if="origin === 'ai'">🤖</span>
            <span v-else>👤</span>
            {{ origin === 'ai' ? 'AI Generated' : 'Human Generated' }}
          </div>

          <!-- Platform badge (TOP) -->
          <div class="platform-chip" :class="platformKey">
            <span>🟧</span>
            WooCommerce (.com)
          </div>
        </div>
      </div>

      <div class="top-actions">
        <!-- 🤖 AI Selector: só em CREATE -->
        <select
          v-if="!isEdit"
          v-model="selectedAI"
          class="select-ai"
          :disabled="saving || generating"
        >
          <option value="deepinfra">🔵 DeepInfra</option>
          <option value="gemini">🟣 Gemini / OpenAI</option>
        </select>

        <!-- ⚡ Generate: só em CREATE -->
        <button
          v-if="!isEdit"
          class="btn btn-secondary"
          :disabled="saving || generating"
          @click="requestAIManual"
          title="Gerar textos/meta com IA (pedido manual)"
        >
          <span v-if="!generating">⚡ Generate</span>
          <span v-else>⏳ Generating…</span>
        </button>

        <button
          class="btn btn-primary"
          :disabled="saving || generating"
          @click="isEdit ? doApprove() : doCreate()"
        >
          {{ isEdit ? 'Approve & Publish' : 'Create & Publish' }}
        </button>
      </div>
    </header>

    <!-- SEARCH PRODUCT FROM DB -->
    <section class="card search-card" v-if="!isEdit">
      <div class="grid-2 compact-gap">
        <div class="col">
          <label>Search Product (EAN or SKU)</label>
          <input
            v-model.trim="searchTerm"
            type="text"
            class="input"
            placeholder="Enter EAN or SKU..."
            @keyup.enter="fetchProductInfo"
          />
        </div>
        <div class="col align-end">
          <button
            class="btn btn-primary"
            :disabled="!searchTerm || loadingSearch"
            @click="fetchProductInfo"
          >
            <span v-if="!loadingSearch">🔍 Search</span>
            <span v-else>⏳ Searching…</span>
          </button>
        </div>
      </div>
    </section>

    <!-- LAYOUT IMG LEFT / FORM RIGHT -->
    <div class="layout">
      <!-- LEFT: IMAGE / META -->
      <aside class="left-pane">
        <div class="img-card">
          <div class="img-wrap">
            <img :src="liveImg" @error="onImgError" alt="Product" />
          </div>

          <!-- AI/Human badge (under image, centered) -->
          <div class="origin-badge-img" :class="origin">
            <span v-if="origin === 'ai'">🤖</span>
            <span v-else>👤</span>
            {{ origin === 'ai' ? 'AI Generated' : 'Human Generated' }}
          </div>

          <div class="mini-grid">
            <div class="mini">
              <span class="k">ID</span>
              <span class="v">#{{ form.id_backoffice || '—' }}</span>
            </div>
            <div class="mini">
              <span class="k">Brand</span>
              <span class="v">{{ suggestedBrand || '-' }}</span>
            </div>
            <div class="mini">
              <span class="k">EAN</span>
              <span class="v">{{ firstEAN }}</span>
            </div>
            <div class="mini">
              <span class="k">Weight</span>
              <span class="v">{{ form.weight || '-' }}</span>
            </div>
          </div>
        </div>

        <!-- QA CARD (LEFT) -->
        <section
          v-if="qa"
          class="qa-card-left"
          :class="qa.approved ? 'ok' : 'bad'"
        >
          <div class="qa-head">
            <div class="qa-title">
              <span class="qa-dot"></span>
              QA Validator
            </div>

            <div class="qa-score">
              <span class="score">{{ qa.score }}</span>
              <span class="label">/ 100</span>
            </div>
          </div>

          <div class="qa-sub">
            <span class="qa-pill" :class="qa.approved ? 'ok' : 'bad'">
              {{ qa.approved ? 'Approved' : 'Needs review' }}
            </span>
            <span class="qa-pill neutral">
              Risk: {{ qa.checks?.claim_risk || '-' }}
            </span>
          </div>

          <p class="qa-summary" v-if="qa.summary">
            {{ qa.summary }}
          </p>

          <div class="qa-grid" v-if="qa.checks">
            <div class="q">
              <span class="k">Structure</span>
              <span class="v">{{
                qa.checks.structure_ok ? 'OK' : 'Fail'
              }}</span>
            </div>
            <div class="q">
              <span class="k">HTML</span>
              <span class="v">{{ qa.checks.html_ok ? 'OK' : 'Fail' }}</span>
            </div>
            <div class="q">
              <span class="k">SEO</span>
              <span class="v">{{ qa.checks.seo_ok ? 'OK' : 'Fail' }}</span>
            </div>
            <div class="q">
              <span class="k">Output</span>
              <span class="v">{{ qa.checks.is_english ? 'EN' : '—' }}</span>
            </div>
          </div>

          <div v-if="qa.issues?.length" class="qa-list">
            <div class="qa-list-title">Issues</div>
            <ul>
              <li v-for="(it, i) in qa.issues" :key="'iss' + i">{{ it }}</li>
            </ul>
          </div>

          <div v-if="qa.fixes?.length" class="qa-list">
            <div class="qa-list-title">Fixes</div>
            <ul>
              <li v-for="(it, i) in qa.fixes" :key="'fix' + i">{{ it }}</li>
            </ul>
          </div>
        </section>

        <!-- Guidelines (sempre visível) -->
        <section class="guidelines-card-left always-open">
          <div class="guidelines-body">
            <p>
              Use only clean HTML: <code>&lt;strong&gt;</code>,
              <code>&lt;ul&gt;</code>, <code>&lt;li&gt;</code> and plain
              paragraphs.
            </p>

            <p>
              <strong>Description structure:</strong><br />
              1) Intro paragraph (40–60 words) starting with
              <strong>&lt;strong&gt;Product Title&lt;/strong&gt;</strong><br />
              2) One USP sentence (15–25 words)<br />
              3) Usage sentence (natural, no “How to use”)
            </p>

            <p>
              <strong>Short description:</strong><br />
              • One paragraph (20–35 words)<br />
              • Then
              <code
                >&lt;ul&gt;&lt;li&gt;5–7 concise
                benefits&lt;/li&gt;&lt;/ul&gt;</code
              >
            </p>

            <p>
              No <code>&lt;br&gt;</code>, no <code>&lt;span&gt;</code>, no
              headings, no images, no EAN.
            </p>
          </div>
        </section>
      </aside>

      <!-- RIGHT: FORM -->
      <main class="right-pane">
        <!-- BASIC & ORIGIN -->
        <section class="card">
          <div class="grid-2 compact-gap">
            <div class="col">
              <label>SKU</label>
              <div class="locked-wrap">
                <input
                  ref="ref_sku_text"
                  v-model="form.sku_text"
                  type="text"
                  class="input with-lock"
                  :readonly="!isUnlocked('sku_text')"
                />
                <button
                  type="button"
                  class="lock-btn"
                  :class="isUnlocked('sku_text') ? 'unlocked' : 'locked'"
                  :title="
                    isUnlocked('sku_text')
                      ? 'Re-bloquear SKU'
                      : 'Desbloquear SKU'
                  "
                  aria-label="Toggle lock SKU"
                  @click="toggleLock('sku_text', 'SKU')"
                >
                  <span v-if="!isUnlocked('sku_text')">🔒</span>
                  <span v-else>🔓</span>
                </button>
              </div>
            </div>

            <div class="col">
              <label>EAN</label>
              <div class="locked-wrap">
                <input
                  ref="ref_ean_text"
                  v-model="form.ean_text"
                  type="text"
                  class="input with-lock"
                  :readonly="!isUnlocked('ean_text')"
                />
                <button
                  type="button"
                  class="lock-btn"
                  :class="isUnlocked('ean_text') ? 'unlocked' : 'locked'"
                  :title="
                    isUnlocked('ean_text')
                      ? 'Re-bloquear EAN'
                      : 'Desbloquear EAN'
                  "
                  aria-label="Toggle lock EAN"
                  @click="toggleLock('ean_text', 'EAN')"
                >
                  <span v-if="!isUnlocked('ean_text')">🔒</span>
                  <span v-else>🔓</span>
                </button>
              </div>
            </div>

            <div class="col">
              <label>Title</label>
              <div class="locked-wrap">
                <input
                  ref="ref_name"
                  v-model="form.name"
                  type="text"
                  class="input with-lock"
                  :readonly="!isUnlocked('name')"
                />
                <button
                  type="button"
                  class="lock-btn"
                  :class="isUnlocked('name') ? 'unlocked' : 'locked'"
                  :title="
                    isUnlocked('name')
                      ? 'Re-bloquear Title'
                      : 'Desbloquear Title'
                  "
                  aria-label="Toggle lock Title"
                  @click="toggleLock('name', 'Title')"
                >
                  <span v-if="!isUnlocked('name')">🔒</span>
                  <span v-else>🔓</span>
                </button>
              </div>
            </div>

            <div class="col">
              <label>Slug</label>
              <div class="locked-wrap">
                <input
                  ref="ref_slug"
                  v-model="form.slug"
                  type="text"
                  class="input with-lock"
                  :readonly="!isUnlocked('slug')"
                />
                <button
                  type="button"
                  class="lock-btn"
                  :class="isUnlocked('slug') ? 'unlocked' : 'locked'"
                  :title="
                    isUnlocked('slug') ? 'Re-bloquear Slug' : 'Desbloquear Slug'
                  "
                  aria-label="Toggle lock Slug"
                  @click="toggleLock('slug', 'Slug')"
                >
                  <span v-if="!isUnlocked('slug')">🔒</span>
                  <span v-else>🔓</span>
                </button>
              </div>
            </div>

            <div class="col">
              <label>HS Code</label>
              <input v-model="form.hscode" type="text" class="input" />
            </div>

            <div class="col">
              <label>Country</label>
              <input v-model="form.country" type="text" class="input" />
            </div>

            <div class="col">
              <label>Weight (g)</label>
              <div class="locked-wrap">
                <input
                  ref="ref_weight"
                  v-model.number="form.weight"
                  type="number"
                  class="input with-lock"
                  :readonly="!isUnlocked('weight')"
                />
                <button
                  type="button"
                  class="lock-btn"
                  :class="isUnlocked('weight') ? 'unlocked' : 'locked'"
                  :title="
                    isUnlocked('weight')
                      ? 'Re-bloquear Weight'
                      : 'Desbloquear Weight'
                  "
                  aria-label="Toggle lock Weight"
                  @click="toggleLock('weight', 'Weight')"
                >
                  <span v-if="!isUnlocked('weight')">🔒</span>
                  <span v-else>🔓</span>
                </button>
              </div>
            </div>

            <div class="col" v-if="!isEdit">
              <label>Price (€)</label>
              <input
                v-model.number="form.price"
                type="number"
                step="0.01"
                min="0"
                class="input"
                placeholder="Opcional (ex: 19.99)"
              />
            </div>

            <!-- IMAGE URL FULL -->
            <div class="col col-full">
              <label>Image URL</label>
              <input
                v-model="form.img"
                type="text"
                class="input img-long"
                @input="debouncedImg()"
                placeholder="https://..."
              />
              <small class="hint">Preview atualiza em tempo real</small>
            </div>

            <!-- BRAND SELECT FULL -->
            <div class="col col-full">
              <label>Brand</label>
              <v-select
                :options="brands"
                v-model="form.brand"
                label="name"
                placeholder="Select brand"
                class="vs"
              />
            </div>

            <!-- BRAND SUGGESTED FULL -->
            <div class="col col-full">
              <div class="ai-label">Suggested Brand</div>
              <div class="ai-chip-row">
                <span v-if="row?.brand" class="ai-chip">{{ row.brand }}</span>
              </div>
            </div>

            <!-- TAGS SELECT FULL -->
            <div class="col col-full">
              <label>Tags</label>
              <v-select
                :options="allTags"
                v-model="form.final_tags"
                label="name"
                placeholder="Select tags"
                multiple
                class="vs"
              />
            </div>

            <!-- TAGS AI FULL -->
            <div class="col col-full">
              <div class="ai-label">Suggested Tags</div>
              <div class="ai-chip-row wrap">
                <span v-for="(tg, i) in readonlyTags" :key="i" class="ai-chip">
                  {{ tg }}
                </span>
              </div>
            </div>
          </div>
        </section>

        <!-- DESCRIPTION LONG -->
        <section class="card">
          <div class="section-head">
            <h3 class="sec-title">Description (HTML)</h3>
          </div>
          <div class="split">
            <div ref="monoLong" class="monaco-host tall"></div>
            <div class="preview-block tall" v-html="previewHTML"></div>
          </div>
        </section>

        <!-- SHORT DESCRIPTION -->
        <section class="card tight">
          <div class="section-head">
            <h3 class="sec-title">Short Description (HTML)</h3>
          </div>
          <div class="split slim">
            <div ref="monoShort" class="monaco-host short"></div>
            <div class="preview-block short" v-html="shortPreviewHTML"></div>
          </div>
        </section>

        <!-- SEO -->
        <section class="card">
          <h3 class="sec-title">SEO</h3>
          <div class="grid-2 compact-gap">
            <div class="col">
              <label>Meta Title</label>
              <input v-model="form.meta_title" type="text" class="input" />
            </div>
            <div class="col">
              <label>Meta Description</label>
              <textarea
                v-model="form.meta_description"
                rows="3"
                class="input"
              ></textarea>
            </div>
          </div>
        </section>
        <!-- FAQ FIELDS -->
        <section class="card">
          <h3 class="sec-title">FAQ (Custom Fields)</h3>
          <div class="grid-2 compact-gap">
            <div class="col col-full">
              <label>O que é</label>
              <textarea
                v-model="form.faq_o_que_e"
                rows="2"
                class="input"
              ></textarea>
            </div>
            <div class="col col-full">
              <label>Para que serve</label>
              <textarea
                v-model="form.faq_para_que_serve"
                rows="2"
                class="input"
              ></textarea>
            </div>
            <div class="col col-full">
              <label>Como se usa</label>
              <textarea
                v-model="form.faq_como_se_usa"
                rows="2"
                class="input"
              ></textarea>
            </div>
          </div>
        </section>
      </main>
    </div>
  </div>

  <div v-else class="loading"><div class="skeleton"></div></div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

// debounce micro para imagem live
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

export default {
  name: 'GeneratedProductEdit',
  props: { id: { type: [String, Number], required: false } },
  components: { 'v-select': vSelect },
  data() {
    return {
      API: `${this.$store.state.apiUrl}`,
      token: localStorage.getItem('token'),
      row: null,
      brands: [],
      allTags: [],
      form: {
        id_backoffice: null,
        name: '',
        brand: '',
        final_tags: [],
        slug: '',
        short_description_html: '',
        description_html: '',
        meta_title: '',
        meta_description: '',
        faq_o_que_e: '',
        faq_para_que_serve: '',
        faq_como_se_usa: '',
        sku_text: '',
        ean_text: '',
        weight: null,
        hscode: '',
        country: '',
        img: '',
        price: null,
      },
      readonlyTags: [],
      suggestedBrand: '',
      saving: false,
      generating: false,
      monacoLoaded: false,
      monaco: null,
      mShort: null,
      mLong: null,
      previewHTML: '',
      shortPreviewHTML: '',
      previewTimer: null,
      previewDelay: 250,
      placeholderImg:
        'https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg',
      liveImg: '',
      debouncedImg: () => {},
      editUnlock: {
        sku_text: false,
        ean_text: false,
        name: false,
        slug: false,
        weight: false,
      },
      origin: 'human',
      searchTerm: '',
      loadingSearch: false,
      // Só o .com edita por aqui (o /eu vem na fase 2; a Shopify saiu do backoffice)
      platform: 'woocommerce',

      qa: null,
      selectedAI: 'deepinfra',
    };
  },
  computed: {
    platformKey() {
      return String(this.platform || '').toLowerCase() === 'shopify'
        ? 'shopify'
        : 'woocommerce';
    },
    isEdit() {
      // se o id for "create" NÃO é edit
      if (this.id === 'create') return false;
      return !!this.id;
    },
    firstEAN() {
      const src = this.form.ean_text || '';
      const parts = String(src)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      return parts[0] || '-';
    },
    backRoute() {
      const from = this.$route.query.from;

      if (from === 'validate') return '/validate-products';
      return '/generated-products'; // default (lista antiga)
    },
  },
  methods: {
    apiUrl(path) {
      const base = `${this.API}${path}`;
      const sep = base.includes('?') ? '&' : '?';
      return `${base}${sep}platform=${encodeURIComponent(this.platform)}`;
    },
    headers() {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      };
    },
    onImgError(e) {
      e.target.src = this.placeholderImg;
    },

    async fetchProductInfo() {
      if (!this.searchTerm) return;
      try {
        this.loadingSearch = true;
        console.log('🔍 Fetching product info for', this.searchTerm);

        const { data } = await axios.post(
          this.apiUrl(`openai/generate-product-info/${this.searchTerm}`),
          {},
          { headers: this.headers() }
        );

        if (!data?.product) {
          Swal.fire(
            'Not Found',
            'No product found with that EAN or SKU',
            'warning'
          );
          return;
        }

        const p = data.product;
        console.log('✅ Product found:', p);

        // 🧠 preencher o form
        this.form.id_backoffice = p.id_backoffice || null;
        this.form.name = p.name || '';
        this.form.ean_text = p.ean || '';
        this.form.sku_text = p.sku || '';
        this.form.brand = p.brand_name || '';
        this.form.hscode = p.hscode; // limpa (será gerado depois)
        this.form.country = p.country; // limpa (será gerado depois)
        this.form.slug = '';
        this.form.short_description_html = '';
        this.form.description_html = '';
        this.form.meta_title = '';
        this.form.meta_description = '';
        this.readonlyTags = [];
        this.form.weight = p.weight || null;

        this.suggestedBrand = p.brand_name || '';

        // 🖼️ imagem do produto (se existir)
        if (p.img) {
          this.form.img = p.img;
          this.liveImg = p.img;
        } else {
          this.form.img = '';
          this.liveImg = this.placeholderImg;
        }
        Swal.fire(
          '✅ Success',
          'Product info loaded from database!',
          'success'
        );
      } catch (e) {
        console.error('❌ Error fetching product info:', e);
        Swal.fire('Error', 'Failed to fetch product info', 'error');
      } finally {
        this.loadingSearch = false;
      }
    },

    // === PREVIEW ===
    lazyPreview(html) {
      if (!html) return '';
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('img').forEach((img) => {
          img.setAttribute('loading', 'lazy');
          img.setAttribute('decoding', 'async');
          img.classList.add('preview-img');
        });
        return doc.body.innerHTML;
      } catch {
        return html;
      }
    },
    scheduleBothPreviews() {
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => {
        this.shortPreviewHTML = this.lazyPreview(
          this.form.short_description_html || ''
        );
        this.previewHTML = this.lazyPreview(this.form.description_html || '');
      }, this.previewDelay);
    },

    // === MONACO ===
    async loadMonacoCDN() {
      if (window.require && window.monaco) {
        this.monacoLoaded = true;
        this.monaco = window.monaco;
        return;
      }
      if (!document.getElementById('monaco-loader')) {
        const s = document.createElement('script');
        s.id = 'monaco-loader';
        s.src = 'https://unpkg.com/monaco-editor@0.45.0/min/vs/loader.js';
        document.head.appendChild(s);
        await new Promise((r) => (s.onload = r));
      }
      window.require.config({
        paths: { vs: 'https://unpkg.com/monaco-editor@0.45.0/min/vs' },
      });
      await new Promise((res) => {
        window.require(['vs/editor/editor.main'], () => {
          this.monaco = window.monaco;
          this.monacoLoaded = true;
          res();
        });
      });
    },
    createEditors() {
      if (!this.monacoLoaded) return;
      if (this.mShort) this.mShort.dispose();
      if (this.mLong) this.mLong.dispose();

      const base = {
        language: 'html',
        theme: 'vs',
        wordWrap: 'on',
        automaticLayout: true,
        minimap: { enabled: false },
        tabSize: 2,
        insertSpaces: true,
      };

      if (this.$refs.monoLong) {
        this.mLong = this.monaco.editor.create(this.$refs.monoLong, {
          ...base,
          value: this.form.description_html || '',
        });
        this.mLong.onDidChangeModelContent(() => {
          this.form.description_html = this.mLong.getValue();
          this.scheduleBothPreviews();
        });
      }

      if (this.$refs.monoShort) {
        this.mShort = this.monaco.editor.create(this.$refs.monoShort, {
          ...base,
          value: this.form.short_description_html || '',
        });
        this.mShort.onDidChangeModelContent(() => {
          this.form.short_description_html = this.mShort.getValue();
          this.scheduleBothPreviews();
        });
      }
    },

    // === LOCK SYSTEM ===
    isUnlocked(key) {
      return !!this.editUnlock[key];
    },
    async toggleLock(key, label) {
      const currentlyUnlocked = this.isUnlocked(key);
      if (currentlyUnlocked) {
        this.$set(this.editUnlock, key, false);
        return;
      }
      const { isConfirmed } = await Swal.fire({
        title: `Desbloquear ${label}?`,
        text: 'Campo sensível (SKU/EAN/Slug/Title/Weight). Continuar?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, desbloquear',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#0f172a',
      });
      if (isConfirmed) {
        this.$set(this.editUnlock, key, true);
        this.$nextTick(() => {
          const el = this.$refs[`ref_${key}`];
          if (el && el.focus) el.focus();
        });
      }
    },

    // === LOAD (EDIT MODE) ===
    async load() {
      try {
        const { data } = await axios.get(
          this.apiUrl(`openai/product-info/${this.id}`),
          { headers: this.headers() }
        );

        if (!data?.product) {
          return Swal.fire('Not found', 'Product not found', 'warning');
        }

        this.row = data.product;
        this.origin = data.product?.is_ai ? 'ai' : 'human';
        this.suggestedBrand = data.product.brand || '';

        // Suggested tags (AI ou BD)
        this.readonlyTags = Array.isArray(data.product.tags)
          ? data.product.tags
          : [];

        // MAIN FORM
        const p = data.product;
        this.qa = data.product.qa_validator_json || null;

        this.form = {
          id_backoffice: p.id_backoffice,
          name: p.name || '',
          brand: p.brand || '',
          final_tags: [],
          slug: p.slug || '',
          short_description_html: p.short_description_html || '',
          description_html: p.description_html || '',
          meta_title: p.meta_title || '',
          meta_description: p.meta_description || '',
          faq_o_que_e: p.faq_o_que_e || '',
          faq_para_que_serve: p.faq_para_que_serve || '',
          faq_como_se_usa: p.faq_como_se_usa || '',
          sku_text: Array.isArray(p.sku) ? p.sku.join(', ') : p.sku || '',
          ean_text: Array.isArray(p.ean) ? p.ean.join(', ') : p.ean || '',
          weight: p.weight ?? null,
          hscode: p.hscode || '',
          country: p.country || '',
          img: p.img || '',
        };

        this.liveImg = this.form.img || this.placeholderImg;

        // === Marcas e categorias ===
        this.brands = data.brands || [];
        this.allTags = Array.isArray(data.categories)
          ? data.categories.map((t) => ({ name: t.name }))
          : [];

        // === Atualizar tags do produto ===
        const dbTags = Array.isArray(p.tags) ? p.tags.map((t) => t.trim()) : [];

        // manter visuais (suggested tags)
        this.readonlyTags = dbTags;

        // associar tags existentes
        this.form.final_tags = this.allTags.filter((tag) =>
          dbTags.some((t) => t.toLowerCase() === tag.name.toLowerCase())
        );

        // adicionar tags que não existam em allTags
        dbTags.forEach((t) => {
          const exists = this.allTags.some(
            (x) => x.name.toLowerCase() === t.toLowerCase()
          );
          if (!exists) {
            const newTag = { name: t };
            this.allTags.push(newTag);
            this.form.final_tags.push(newTag);
          }
        });

        console.log('✅ Tags carregadas do produto:', this.form.final_tags);

        await this.$nextTick();
        if (!this.monacoLoaded) await this.loadMonacoCDN();
        this.createEditors();
        this.scheduleBothPreviews();
      } catch (e) {
        console.error('Erro ao carregar produto:', e);
        Swal.fire('Error', 'Failed to load product', 'error');
      }
    },

    // === ⚡ Generate (manual) ===
    async requestAIManual() {
      if (this.isEdit) return; // só no modo criar

      try {
        this.generating = true;

        const payload = {
          platform: this.platform,
          sku: this.form.sku_text,
          ean: this.form.ean_text,
          name: this.form.name,
          brand_name:
            typeof this.form.brand === 'object'
              ? this.form.brand.name
              : this.form.brand,
          tags:
            this.form.final_tags?.map((t) =>
              typeof t === 'object' ? t.name : t
            ) || [],
          country: this.form.country,
        };

        console.log('Requesting AI generation with payload:', payload);

        const endpointMap = {
          deepinfra: 'openai/generate-product-description-deepinfra',
          gemini: 'openai/generate-product-description',
        };

        const { data } = await axios.post(
          this.apiUrl(endpointMap[this.selectedAI] || endpointMap['deepinfra']),
          payload,
          { headers: this.headers() }
        );

        if (!data?.generated_info) {
          throw new Error('Falha ao gerar informações');
        }

        const info = data.generated_info;
        console.log('Received generated info:', info);
        this.qa = data.qa_result || null;

        // === Atualizar tudo o que veio ===
        this.form.name = info.product_title || this.form.name;
        this.form.slug = info.slug || this.form.slug || '';
        this.form.description_html = info.description_html || '';
        this.form.short_description_html = info.short_description_html || '';
        this.form.meta_title = info.meta_title || '';
        this.form.meta_description = info.meta_description || '';
        this.form.faq_o_que_e = info.faq?.faq_o_que_e || '';
        this.form.faq_para_que_serve = info.faq?.faq_para_que_serve || '';
        this.form.faq_como_se_usa = info.faq?.faq_como_se_usa || '';

        // === Atualizar tags ===
        if (Array.isArray(info.tags) && info.tags.length) {
          this.readonlyTags = info.tags;

          // Normaliza tags da IA
          const normalizedTags = info.tags.map((t) => ({
            name: typeof t === 'string' ? t.trim() : t.name?.trim(),
          }));

          // Adiciona ao allTags se ainda não existir
          normalizedTags.forEach((tag) => {
            const exists = this.allTags.some(
              (x) => x.name.toLowerCase() === tag.name.toLowerCase()
            );
            if (!exists) this.allTags.push(tag);
          });

          // 🔥 Muito importante: agora seleciona com base nos objetos de allTags existentes
          this.form.final_tags = this.allTags.filter((existing) =>
            normalizedTags.some(
              (suggested) =>
                suggested.name.toLowerCase() === existing.name.toLowerCase()
            )
          );

          console.log('✅ Final tags set:', this.form.final_tags);
        }

        // === Atualizar brand ===
        if (info.brand) {
          this.suggestedBrand = info.brand;
          this.form.brand = info.brand;
        }

        // === Atualizar outros possíveis campos (se vierem no futuro) ===
        if (info.weight) this.form.weight = info.weight;

        // 🧾 Atualizar HS Code (IA)
        if (info.hs_code) {
          this.form.hscode = info.hs_code;
          console.log('🧾 HS Code atualizado:', info.hs_code);
        }

        // 🌍 Atualizar Country (IA)
        if (info.country_of_origin) {
          this.form.country = info.country_of_origin;
          console.log('🌍 Country atualizado:', info.country_of_origin);
        }

        // Imagem se vier
        if (info.img) {
          this.form.img = info.img;
          this.liveImg = info.img;
        }

        // 🧠 força Monaco a atualizar o conteúdo
        if (this.mLong) this.mLong.setValue(this.form.description_html);
        if (this.mShort) this.mShort.setValue(this.form.short_description_html);

        this.scheduleBothPreviews();

        Swal.fire('✅ Sucesso', 'Informações geradas com IA!', 'success');
      } catch (e) {
        console.error('Erro ao gerar:', e);
        Swal.fire('Erro', e?.message || 'Falha ao gerar com IA', 'error');
      } finally {
        this.generating = false;
      }
    },

    // === APPROVE & PUBLISH (EDIT) ===
    async doApprove() {
      try {
        // -------- REQUIRED FIELDS VALIDATION --------
        const errors = [];

        const isShopify = String(this.platform).toLowerCase() === 'shopify';

        if (!this.form.name) errors.push('Product name');
        if (!this.form.slug) errors.push('Slug');
        if (!this.form.brand) errors.push('Brand');
        if (!this.form.sku_text) errors.push('SKU');
        if (!this.form.ean_text) errors.push('EAN');
        if (!this.form.description_html) errors.push('Full description');
        if (!isShopify && !this.form.short_description_html)
          errors.push('Short description');
        if (!this.form.meta_title) errors.push('Meta title');
        if (!this.form.meta_description) errors.push('Meta description');
        if (!this.form.img) errors.push('Main image');
        if (!this.form.weight) errors.push('Weight');

        // (blocked on backend) -> country is intentionally not validated

        // Show errors if any field is missing
        if (errors.length > 0) {
          await Swal.fire({
            icon: 'error',
            title: 'Missing Required Fields',
            html: `
          <p>Please fill in the following fields before approving:</p>
          <ul style="text-align: left;">
            ${errors.map((e) => `<li><b>${e}</b></li>`).join('')}
          </ul>
        `,
            confirmButtonText: 'Ok',
          });
          return;
        }

        // -------- CONFIRMATION --------
        const confirm = await Swal.fire({
          title: 'Approve Product?',
          html: 'Are you sure you want to approve and update this product?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Yes, approve',
          cancelButtonText: 'Cancel',
        });

        if (!confirm.isConfirmed) return;

        this.saving = true;

        const payload = {
          platform: this.platform,
          ...this.form,
          sku: this.form.sku_text?.split(',').map((s) => s.trim()) || [],
          ean: this.form.ean_text?.split(',').map((s) => s.trim()) || [],
        };

        // ❌ Remover country (bloqueado)
        delete payload.country;

        const save = await axios.put(
          this.apiUrl(`openai/product-info/${this.form.id_backoffice}`),
          payload,
          {
            headers: this.headers(),
            validateStatus: (s) => s >= 200 && s < 500,
          }
        );

        if (save.status >= 200 && save.status < 300) {
          Swal.fire('✅ Sucesso', 'Produto atualizado!', 'success');
          this.$router.push(this.backRoute);
        } else {
          Swal.fire('Erro', save.data?.message || 'Falha ao guardar', 'error');
        }
      } finally {
        this.saving = false;
      }
    },
    // === CREATE & PUBLISH (manual) ===
    async doCreate() {
      try {
        //Add swal confirmation and check if all the forms are filled
        // -------- REQUIRED FIELDS VALIDATION --------
        const errors = [];

        const isShopify = String(this.platform).toLowerCase() === 'shopify';

        if (!this.form.name) errors.push('Product name');
        if (!this.form.slug) errors.push('Slug');
        if (!this.form.brand) errors.push('Brand');
        if (!this.form.sku_text) errors.push('SKU');
        if (!this.form.ean_text) errors.push('EAN');
        if (!this.form.description_html) errors.push('Full description');
        if (!isShopify && !this.form.short_description_html)
          errors.push('Short description');
        if (!this.form.meta_title) errors.push('Meta title');
        if (!this.form.meta_description) errors.push('Meta description');
        if (!this.form.img) errors.push('Main image');
        if (!this.form.country) errors.push('Country of origin');
        if (!this.form.weight) errors.push('Weight');

        // If any field is missing → show a single Swal listing all missing fields
        if (errors.length > 0) {
          await Swal.fire({
            icon: 'error',
            title: 'Missing Required Fields',
            html: `
          <p>Please fill in the following fields:</p>
          <ul style="text-align: left;">
            ${errors.map((e) => `<li><b>${e}</b></li>`).join('')}
          </ul>
        `,
            confirmButtonText: 'Ok',
          });
          return;
        }

        // -------- CONFIRMATION BEFORE CREATING --------
        const confirm = await Swal.fire({
          title: 'Create Product?',
          html: 'Are you sure you want to create this product?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Yes, create product',
          cancelButtonText: 'Cancel',
        });

        if (!confirm.isConfirmed) return;
        this.saving = true;

        // Converter arrays/textos
        const skuArray = this.form.sku_text
          ? this.form.sku_text
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
        const eanArray = this.form.ean_text
          ? this.form.ean_text
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];

        // Extrair o brand ID se for um objeto (v-select)
        const brand_id =
          typeof this.form.brand === 'object' ? this.form.brand.id : null;
        const brand_name =
          typeof this.form.brand === 'object'
            ? this.form.brand.name
            : this.form.brand;

        // Extrair tags (nomes apenas)
        const tags =
          this.form.final_tags?.map((t) =>
            typeof t === 'object' ? t.name : t
          ) || [];

        // Construir payload completo
        const payload = {
          platform: this.platform,
          // product core info
          sku: skuArray,
          ean: eanArray,
          name: this.form.name,
          slug: this.form.slug,
          brand_id,
          brand_name,
          weight: this.form.weight || null,
          hscode: this.form.hscode || null,
          country: this.form.country || null,
          // 👇 só entra se existir
          ...(this.form.price ? { price: Number(this.form.price) } : {}),

          // AI + description data
          description_html: this.form.description_html,
          short_description_html: this.form.short_description_html,
          meta_title: this.form.meta_title,
          meta_description: this.form.meta_description,
          faq_o_que_e: this.form.faq_o_que_e || null,
          faq_para_que_serve: this.form.faq_para_que_serve || null,
          faq_como_se_usa: this.form.faq_como_se_usa || null,

          // image / misc
          img: this.form.img,
          tags: tags,
          final_tags: tags,

          // flags
          is_ai: false, // criação manual
        };

        console.log('Creating product with payload:', payload);

        const { data, status } = await axios.put(
          this.apiUrl('openai/generate-product-description'),
          payload,
          {
            headers: this.headers(),
            validateStatus: (s) => s >= 200 && s < 500,
          }
        );

        if (status >= 200 && status < 300) {
          Swal.fire('✅ Sucesso', 'Produto criado e publicado!', 'success');
          this.$router.push(this.backRoute);
        } else {
          Swal.fire('Erro', data?.message || 'Falha ao criar produto', 'error');
        }
      } catch (e) {
        console.error('Erro ao criar produto:', e);
        Swal.fire(
          'Erro',
          e?.message || 'Falha inesperada ao criar produto',
          'error'
        );
      } finally {
        this.saving = false;
      }
    },

    // === Fetch brands + categories (num único request) ===
    async fetchBrandsAndTags() {
      try {
        const { data } = await axios.get(
          this.apiUrl('openai/categories-brands'),
          { headers: this.headers() }
        );

        // Atualizar lista de marcas
        this.brands = Array.isArray(data?.brands)
          ? data.brands.map((b) => ({
              id: b.id,
              name: b.name,
            }))
          : [];

        // Atualizar lista de categorias/tags
        this.allTags = Array.isArray(data?.categories)
          ? data.categories.map((c) => ({
              name: c.name,
              woo_id: c.woo_id,
            }))
          : [];

        console.log('✅ Brands e categorias carregadas', {
          brands: this.brands.length,
          tags: this.allTags.length,
        });
      } catch (e) {
        console.error('Erro ao carregar brands/categories:', e);
        Swal.fire('Erro', 'Falha ao carregar marcas e categorias', 'error');
      }
    },
  },

  async created() {
    localStorage.setItem('gen_platform', this.platform);
    this.debouncedImg = debounce(() => {
      this.liveImg = this.form.img || this.placeholderImg;
    }, 200);

    // 👉 carregar marcas e tags logo no arranque
    await this.fetchBrandsAndTags();

    if (this.isEdit) {
      await this.load();
    } else {
      // === CREATE MODE ===
      this.origin = 'human';
      this.liveImg = this.placeholderImg;
      this.form.price = null;

      // 🔓 Desbloquear todos os campos no modo criar
      Object.keys(this.editUnlock).forEach((key) => {
        this.editUnlock[key] = true;
      });

      await this.$nextTick();
      if (!this.monacoLoaded) await this.loadMonacoCDN();
      this.createEditors();
      this.scheduleBothPreviews();
    }
  },

  watch: {
    'form.description_html'() {
      this.scheduleBothPreviews();
    },
    'form.short_description_html'() {
      this.scheduleBothPreviews();
    },
  },
};
</script>

<style scoped>
/* Base */
.gen-edit {
  max-width: 1320px;
  margin: 0 auto;
  padding: 22px;
  font-family: 'Inter', system-ui, -apple-system, Segoe UI, Roboto,
    'Helvetica Neue', Arial, sans-serif;
  color: #0f172a;
}

/* Topbar */
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eaeef4;
}
.topbar .left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.top-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.pill {
  background: #eef2f7;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 2px 8px; /* encurtado */
  font-size: 11px;
  font-weight: 800;
  color: #334155;
}
.title {
  font-size: 20px;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.01em;
  max-width: 60ch;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Buttons */
.btn {
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  font-weight: 600;
}
.btn-ghost {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #0f172a;
}
.btn-ghost:hover {
  background: #eef2f7;
}
.btn-primary {
  background: #0f172a;
  color: #fff;
}
.btn-primary:hover {
  background: #0b1220;
}
.btn-secondary {
  background: #ffffff;
  color: #0f172a;
  border-color: #cbd5e1;
}
.btn-secondary:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

/* LAYOUT IMG LEFT */
.layout {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 18px;
}
@media (max-width: 1080px) {
  .layout {
    grid-template-columns: 1fr;
  }
}

/* LEFT SIDE */
.left-pane {
  position: sticky;
  top: 12px;
  align-self: start;
}
.img-card {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 1px 2px rgba(4, 15, 35, 0.03);
}
.img-wrap {
  width: 100%;
  aspect-ratio: 1/1;
  background: #f8fafc;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  display: grid;
  place-items: center;
  overflow: hidden;
  margin-bottom: 10px;
}
.img-wrap img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.mini-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px 8px;
}
.mini {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}
.k {
  color: #64748b;
}
.v {
  font-weight: 600;
  color: #0f172a;
}

/* CARDS */
.card {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 1px 2px rgba(4, 15, 35, 0.03);
  margin-bottom: 18px;
}

/* GRID */
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 16px;
}
@media (max-width: 900px) {
  .grid-2 {
    grid-template-columns: 1fr;
  }
}
.compact-gap {
  gap: 12px;
}
.col {
  display: flex;
  flex-direction: column;
}
.col-2 {
  grid-column: span 2;
}

.col-full {
  grid-column: 1 / -1 !important;
}
.ai-chip-row.wrap {
  flex-wrap: wrap;
  display: flex;
  flex-direction: row;
  row-gap: 6px;
}

/* LABELS */
label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 4px;
  color: #475569;
}

/* INPUTS */
.input {
  width: 100%;
  border: 1px solid #ccd3e0;
  background: #fff;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
}
.input:focus {
  outline: none;
  border-color: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.18);
}

/* LONG IMAGE URL FIELD */
.img-long {
  width: 100%;
}
.hint {
  margin-top: 4px;
  font-size: 11px;
  color: #64748b;
}

/* v-select base */
.vs {
  --vs-border-color: #ccd3e0;
  --vs-border-radius: 10px;
  --vs-dropdown-bg: #fff;
  --vs-font-size: 13px;
}
.vs .vs__selected-options {
  padding-bottom: 2px !important;
}
.vs .vs__dropdown-menu {
  max-height: 200px;
  overflow-y: auto;
}

/* AI SUGGESTIONS */
.ai-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 2px;
}
.ai-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}
.ai-chip {
  background: #f1f5f9;
  border: 1px dashed #d3dae4;
  border-radius: 9999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  pointer-events: none;
}

/* ===== MONACO + PREVIEW (55/45 EXACT) ===== */
.split {
  display: grid;
  grid-template-columns: 55% 45%;
  gap: 14px;
}
.monaco-host {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  height: 440px;
}
.monaco-host.short {
  height: 320px;
}
.preview-block {
  background: #fafafa;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  padding: 14px;
  height: 440px;
  overflow-y: auto;
}
.preview-block.short {
  height: 320px;
}
.preview-block .preview-img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 8px auto;
}
.preview-block :where(p, li, strong, em, h4, h3) {
  -webkit-font-smoothing: antialiased;
  color: #1e293b;
}

/* LOADING */
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px 0;
}
.skeleton {
  width: 70%;
  height: 130px;
  border-radius: 14px;
  background: linear-gradient(90deg, #f6f7f9 25%, #eceff3 37%, #f6f7f9 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: 0 0;
  }
}

/* ===== 🔒 Locked Input ===== */
.locked-wrap {
  position: relative;
}
.input.with-lock {
  padding-right: 48px;
}
.lock-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: 1px solid #e2e8f0;
  background: #eef2f7;
  color: #0f172a;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
  user-select: none;
}
.lock-btn:hover {
  background: #e6ecf4;
  border-color: #d6dee9;
}
.lock-btn.locked {
  opacity: 0.9;
}
.lock-btn.unlocked {
  background: #e8fff1;
  border-color: #9ee2bf;
}
.lock-btn.unlocked:hover {
  background: #dcfde9;
}

/* ===== Guidelines Left Card (Always Visible) ===== */
.guidelines-card-left {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 12px 14px;
  margin-top: 14px;
}
.guidelines-card-left .guidelines-body {
  font-size: 12.5px;
  color: #334155;
  line-height: 1.45;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.guidelines-card-left code {
  background: #e2e8f0;
  padding: 1px 4px;
  border-radius: 4px;
  font-size: 12px;
}

/* ===== Origin Chips (TOP + under image) ===== */
.gen-origin-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 14px;
  margin-left: 10px;
  height: fit-content;
  line-height: 1;
  border: 1px solid transparent;
  letter-spacing: 0.3px;
  gap: 6px;
}
.gen-origin-chip.ai {
  background: #dbeafe;
  color: #1e3a8a;
  border-color: #93c5fd;
}
.gen-origin-chip.human {
  background: #ecfdf5;
  color: #065f46;
  border-color: #6ee7b7;
}

/* Origin Chip under image (centered) */
.origin-badge-img {
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 14px;
  border: 1px solid transparent;
  letter-spacing: 0.3px;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
}
.origin-badge-img.ai {
  background: #dbeafe;
  color: #1e3a8a;
  border-color: #93c5fd;
}
.origin-badge-img.human {
  background: #ecfdf5;
  color: #065f46;
  border-color: #6ee7b7;
}

.search-card {
  margin-bottom: 20px;
}
.align-end {
  display: flex;
  align-items: flex-end;
}
/* ===== Platform Chips (TOP + under image) ===== */
.platform-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 14px;
  margin-left: 10px;
  height: fit-content;
  line-height: 1;
  border: 1px solid transparent;
  letter-spacing: 0.3px;
  gap: 6px;
}

.platform-chip.shopify {
  background: #ede9fe; /* roxo clarinho */
  color: #5b21b6;
  border-color: #c4b5fd;
}

.platform-chip.woocommerce {
  background: #ffedd5; /* laranja clarinho */
  color: #9a3412;
  border-color: #fdba74;
}

/* Under image (centered like origin) */
.platform-badge-img {
  margin-top: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 14px;
  border: 1px solid transparent;
  letter-spacing: 0.3px;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
}

.platform-badge-img.shopify {
  background: #ede9fe;
  color: #5b21b6;
  border-color: #c4b5fd;
}

.platform-badge-img.woocommerce {
  background: #ffedd5;
  color: #9a3412;
  border-color: #fdba74;
}

/* ===== QA CARD (LEFT) ===== */
.qa-card-left {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 12px 14px;
  margin-top: 14px;
  box-shadow: 0 1px 2px rgba(4, 15, 35, 0.03);
}

.qa-card-left.ok {
  border-color: #86efac;
  background: #f0fdf4;
}

.qa-card-left.bad {
  border-color: #fecaca;
  background: #fff1f2;
}

.qa-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.qa-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 900;
  font-size: 12px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #0f172a;
}

.qa-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #64748b;
}

.qa-card-left.ok .qa-dot {
  background: #16a34a;
}
.qa-card-left.bad .qa-dot {
  background: #dc2626;
}

.qa-score .score {
  font-size: 18px;
  font-weight: 900;
}
.qa-score .label {
  font-size: 12px;
  color: #475569;
  margin-left: 4px;
  font-weight: 700;
}

.qa-sub {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.qa-pill {
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid transparent;
}

.qa-pill.ok {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}

.qa-pill.bad {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fecaca;
}

.qa-pill.neutral {
  background: #eef2f7;
  color: #334155;
  border-color: #e2e8f0;
}

.qa-summary {
  margin: 0 0 10px;
  font-size: 12.5px;
  color: #334155;
  line-height: 1.45;
}

.qa-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px 10px;
  margin-bottom: 10px;
}

.q {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}

.q .k {
  color: #64748b;
  font-weight: 800;
}

.q .v {
  font-weight: 900;
  color: #0f172a;
}

.qa-list {
  margin-top: 8px;
}

.qa-list-title {
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #0f172a;
  margin-bottom: 4px;
}

.qa-list ul {
  margin: 0;
  padding-left: 18px;
}

.qa-list li {
  font-size: 12px;
  color: #334155;
  line-height: 1.35;
  margin-bottom: 4px;
}

/* ===== AI Selector ===== */
.select-ai {
  height: 36px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  background: #fff;
  cursor: pointer;
  color: #0f172a;
  transition: border-color 0.15s ease;
}

.select-ai:hover {
  border-color: #94a3b8;
}

.select-ai:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
